import { SAVED_DATA_VERSION } from '../data/constants';

export const validateWordReadingData = (data) => {
  const parentName = 'realwordReadingState';
  const errorMessage = createErrorMessage();
  if (validateObject(data, parentName, errorMessage)) {
    // empty object is ok
    if (Object.keys(data).length === 0) {
      return true;
    }
    const {
      questionIndex,
      responses,
      numberIncorrect,
      questionsCorrectByItemType,
    } = data;
    validateNumber(questionIndex, `${parentName}.questionIndex`, errorMessage);
    validateObject(responses, `${parentName}.responses`, errorMessage);
    validateNumber(numberIncorrect, `${parentName}.numberIncorrect`, errorMessage);

    if (questionsCorrectByItemType) {
      validateObject(questionsCorrectByItemType, `${parentName}.questionsCorrectByItemType`, errorMessage);
      Object.entries(questionsCorrectByItemType).forEach(([itemType, questionsCorrect]) => {
        validateNumber(questionsCorrect, `${parentName}.questionsCorrectByItemType.${itemType}`, errorMessage);
      });
    }
  }

  const errorsCount = errorMessage.getErrorCount();
  if (errorsCount > 0) {
    throw new Error(`Encountered ${errorsCount} error(s) while validating data: \n ${errorMessage.get()}`);
  }

  return true;
};

export const validatePlacementToolData = (data) => {
  const parentName = 'placementToolState';
  const errorMessage = createErrorMessage();
  if (validateObject(data, parentName, errorMessage)) {
    // empty object is ok
    if (Object.keys(data).length === 0) {
      return true;
    }
    const {
      version,
      results,
      currentMeasure,
      measureInProgress,
      measuresCompleted,
      measuresRemaining,
      placements,
      rwrId,
      mtgIdStory1,
      mtgIdStory2,
      benchmarkId,
    } = data;

    validateDataVersion(version, `${parentName}.version`, errorMessage);

    // validating result property
    if (validateArray(results, `${parentName}.results`, errorMessage)) {
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const resultName = `${parentName}.results[${i}]`;
        validateMeasure(result, resultName, errorMessage);
        validateObject(result.questions, `${resultName}.questions`, errorMessage);
        validateString(result.score, `${resultName}.score`, errorMessage);
        const validScores = ['blue', 'green', 'red'];
        validateEnum(result.score, validScores, `${resultName}.score`, errorMessage);
        if (result.total_score !== undefined) {
          validateNumber(result.total_score, `${resultName}.total_score`, errorMessage);
        }
        if (result.possible_score !== undefined) {
          validateNumber(result.possible_score, `${resultName}.posible_score`, errorMessage);
        }
      }
    }

    validateMeasure(currentMeasure, `${parentName}.currentMeasure`, errorMessage);
    validateMeasure(measureInProgress, `${parentName}.measureInProgress`, errorMessage);
    validateArrayOfMeasures(measuresCompleted, `${parentName}.measuresCompleted`, errorMessage);
    validateArrayOfMeasures(measuresRemaining, `${parentName}.measuresRemaining`, errorMessage);
    validateObject(placements, `${parentName}.placements`, errorMessage);
    validateSessionId((rwrId || null), `${parentName}.rwrId`, errorMessage);
    validateSessionId((mtgIdStory1 || null), `${parentName}.mtgIdStory1`, errorMessage);
    validateSessionId((mtgIdStory2 || null), `${parentName}.mtgIdStory2`, errorMessage);
    validateSessionId((benchmarkId || null), `${parentName}.benchmarkId`, errorMessage);
  }

  const errorsCount = errorMessage.getErrorCount();
  if (errorsCount > 0) {
    throw new Error(`Encountered ${errorsCount} error(s) while validating data: \n ${errorMessage.get()}`);
  }
  return true;
};

const validateArrayOfMeasures = (measures, name, errorMessage) => {
  if (!validateArray(measures, name, errorMessage)) {
    return false;
  }
  let errorsCount = 0;
  for (let i = 0; i < measures.length; i++) {
    if (!validateMeasure(measures[i], `${name}[${i}]`, errorMessage)) { errorsCount++; }
  }
  if (errorsCount > 0) {
    return false;
  }
  return true;
};

const validateMeasure = (measure, name, errorMessage) => {
  let errorsCount = 0;

  if (validateObject(measure, name, errorMessage)) {
    // empty object is ok
    if (Object.keys(measure).length === 0) {
      return true;
    }
    if (!validateString(measure.id, `${name}.id`, errorMessage)) { errorsCount++; }
    if (!validateString(measure.measure, `${name}.measure`, errorMessage)) { errorsCount++; }
    if (!validateObject(measure.scoring, `${name}.scoring`, errorMessage)) { errorsCount++; }

    if (
      measure.scoring.blue !== undefined &&
      !isNumberOrArrayOfNumbers(measure.scoring.blue, `${name}.scoring.blue`, errorMessage)
    ) { errorsCount++; }
    if (
      measure.scoring.red !== undefined &&
      !isNumberOrArrayOfNumbers(measure.scoring.green, `${name}.scoring.green`, errorMessage)
    ) { errorsCount++; }
    if (
      measure.scoring.green !== undefined &&
      !isNumberOrArrayOfNumbers(measure.scoring.green, `${name}.scoring.green`, errorMessage)
    ) { errorsCount++; }

    if (!validateString(measure.curriculum, `${name}.curriculum`, errorMessage)) { errorsCount++; }
    if (!validateString(measure.submeasure, `${name}.submeasure`, errorMessage)) { errorsCount++; }
    if (errorsCount > 0) {
      return false;
    }
  } else {
    // if it's not a valid object, it's not ok, error!
    if (errorMessage) {
      errorMessage.add(`- invalid ${name}, should be an object but its value is: ${measure}`);
    }
    return false;
  }

  return true;
};

const isNumberOrArrayOfNumbers = (value, name, errorMessage) => {
  let errorsCount = 0;
  if (validateArray(value)) {
    value.forEach((element, i) => {
      if (!validateNumber(element)) {
        if (errorMessage) {
          errorsCount++;
          errorMessage.add(
            `- invalid ${name}, should be an array of numbers, but the [${i}] has a value of: ${element}`,
          );
        }
      }
    });
  } else if (!validateNumber(value)) {
    if (errorMessage) {
      errorsCount++;
      errorMessage.add(`- invalid ${name}, should be an array of numbers or a number, but its value is: ${value}`);
    }
  }
  return (errorsCount === 0);
};

const validateString = (value, name, errorMessage) => {
  if (typeof (value) !== 'string') {
    if (errorMessage) {
      errorMessage.add(`- invalid ${name}, should be a string but its value is: ${value}`);
    }
    return false;
  }
  return true;
};

const validateObject = (value, name, errorMessage) => {
  if (typeof (value) !== 'object') {
    if (errorMessage) {
      errorMessage.add(`- invalid ${name}, should be an object but its value is: ${value}`);
    }
    return false;
  }
  return true;
};

const validateArray = (value, name, errorMessage) => {
  if (!Array.isArray(value)) {
    if (errorMessage) {
      errorMessage.add(`- invalid ${name} should be an array but its value is: ${value}`);
    }
    return false;
  }
  return true;
};

const validateNumber = (value, name, errorMessage) => {
  if (typeof (value) !== 'number') {
    if (errorMessage) {
      errorMessage.add(`- invalid ${name}, should be a number but its value is: ${value}`);
    }
    return false;
  }
  return true;
};

const validateEnum = (value, enumList, name, errorMessage) => {
  if (!enumList.includes(value)) {
    if (errorMessage) {
      errorMessage.add(`- invalid ${name}, should be an enum of [${enumList}] but its value is: ${value}`);
    }
    return false;
  }
  return true;
};

const createErrorMessage = () => {
  let message = '';
  let count = 0;
  const add = (m) => {
    message += `\n ${m}`;
    count++;
  };
  const get = () => message;
  const getErrorCount = () => count;
  return {
    get,
    getErrorCount,
    message,
    add,
  };
};

export const validateSessionId = (sessionId) => {
  const isValidSessionId = /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(sessionId)
    || sessionId === null;

  if (!isValidSessionId) {
    throw new Error('Invalid Session ID format');
  }

  return true;
};

const validateDataVersion = (value, name, errorMessage) => {
  if (value !== SAVED_DATA_VERSION) {
    if (errorMessage) {
      errorMessage.add(
        `- invalid ${name}, should match data version "${SAVED_DATA_VERSION}",  but its value is: ${value}`,
      );
    }
    return false;
  }
  return true;
};
